import { useCallback, useEffect, useMemo, useState } from 'react';
import { trpClient } from '../client/trpClient';
import { OrgMemberOption, RFQAccessList, RFQRole } from '../types';
import { apiErrorMessage } from '../utils/apiErrorMessage';

/**
 * Everything the Share modal knows: who has access, who could be given it, and
 * the two changes an owner can make.
 *
 * Adding is STAGED, not immediate. Picking a colleague turns them into a chip
 * in the search field; the Share button is what actually grants access, to
 * everyone staged at once and all with the chosen role. Sharing an RFQ hands
 * someone the ability to email suppliers and accept an award — a decision that
 * deserves a deliberate second step rather than firing on the first click.
 *
 * Refetch rather than optimistic patching. Sharing is rare and deliberate, and
 * showing a row the server then refuses would be exactly the dishonest UI this
 * product bans; the round trip is one small request.
 */

/** How the search matches: a name or an address containing what was typed. */
export const matchesQuery = (member: OrgMemberOption, query: string): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return `${member.name ?? ''} ${member.email ?? ''}`.toLowerCase().includes(needle);
};

export interface RfqSharing {
  access: RFQAccessList | null;
  /** Everyone in the org, each carrying the standing they already hold. */
  members: OrgMemberOption[];
  /** `members` narrowed by the current query — what the results list renders. */
  matches: OrgMemberOption[];
  /** The people picked but not yet shared with — the chips in the field. */
  staged: OrgMemberOption[];
  toggleStaged: (userId: string) => void;
  unstage: (userId: string) => void;
  /** Backspace on an empty field drops the chip nearest the cursor. */
  unstageLast: () => void;
  query: string;
  setQuery: (query: string) => void;
  role: RFQRole;
  setRole: (role: RFQRole) => void;
  loading: boolean;
  /** True while the staged batch is being granted. */
  sharing: boolean;
  /** The collaborator currently being removed, for its row's spinner. */
  removingUserId: string | null;
  /** Robbie's words for whatever went wrong, ready to render as-is. */
  error: string | null;
  shareStaged: () => Promise<void>;
  removeCollaborator: (userId: string) => Promise<void>;
}

/**
 * @param rfqId - the RFQ being shared
 * @param open - the modal's state; nothing is fetched until it opens, and the
 *   list is re-read on each open so it is never a stale snapshot from earlier
 *   in the session.
 */
export const useRfqSharing = (rfqId: string | undefined, open: boolean): RfqSharing => {
  const [access, setAccess] = useState<RFQAccessList | null>(null);
  const [members, setMembers] = useState<OrgMemberOption[]>([]);
  const [stagedIds, setStagedIds] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [role, setRole] = useState<RFQRole>('EDITOR');
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!rfqId) return;
    setLoading(true);
    setError(null);
    try {
      // In parallel — neither answer depends on the other, and the modal has
      // nothing to show until both have landed.
      const [nextAccess, nextMembers] = await Promise.all([
        trpClient.getRfqAccess(rfqId),
        trpClient.getRfqCollaboratorCandidates(rfqId),
      ]);
      setAccess(nextAccess);
      setMembers(nextMembers);
    } catch (e) {
      setError(apiErrorMessage(e, "I couldn't load who has access to this RFQ just now. Close this and try again."));
    } finally {
      setLoading(false);
    }
  }, [rfqId]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setStagedIds([]);
    load();
  }, [open, load]);

  const matches = useMemo(() => members.filter((m) => matchesQuery(m, query)), [members, query]);

  // Staging holds ids, not member objects: a refetch replaces every object, and
  // chips keyed to the old ones would go stale the moment the list reloads.
  const staged = useMemo(
    () => stagedIds.map((id) => members.find((m) => m.userId === id)).filter((m): m is OrgMemberOption => Boolean(m)),
    [stagedIds, members],
  );

  const toggleStaged = useCallback((userId: string) => {
    setStagedIds((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
  }, []);

  const unstage = useCallback((userId: string) => {
    setStagedIds((prev) => prev.filter((id) => id !== userId));
  }, []);

  const unstageLast = useCallback(() => setStagedIds((prev) => prev.slice(0, -1)), []);

  /**
   * Grant access to everyone staged. The calls run one after another so a
   * refusal names the person it belongs to; the ones that succeeded stay
   * granted rather than being rolled back, and their chips clear while the
   * failures keep theirs so the owner can see what is left to do.
   */
  const shareStaged = useCallback(async () => {
    if (!rfqId || stagedIds.length === 0) return;
    setSharing(true);
    setError(null);

    const failures: string[] = [];
    const granted: string[] = [];
    for (const userId of stagedIds) {
      try {
        await trpClient.addRfqCollaborator(rfqId, userId, role);
        granted.push(userId);
      } catch (e) {
        const who = members.find((m) => m.userId === userId);
        failures.push(`${who?.name ?? who?.email ?? 'someone on your team'}: ${apiErrorMessage(e, 'I could not share with them.')}`);
      }
    }

    setStagedIds((prev) => prev.filter((id) => !granted.includes(id)));
    if (failures.length > 0) setError(failures.join(' '));
    else setQuery('');

    await load();
    setSharing(false);
  }, [rfqId, stagedIds, role, members, load]);

  const removeCollaborator = useCallback(
    async (userId: string) => {
      if (!rfqId) return;
      setRemovingUserId(userId);
      setError(null);
      try {
        await trpClient.removeRfqCollaborator(rfqId, userId);
        await load();
      } catch (e) {
        setError(
          apiErrorMessage(e, "I couldn't remove their access just now, so nothing changed. Try again in a moment."),
        );
      } finally {
        setRemovingUserId(null);
      }
    },
    [rfqId, load],
  );

  return {
    access, members, matches, staged, toggleStaged, unstage, unstageLast,
    query, setQuery, role, setRole, loading, sharing, removingUserId, error,
    shareStaged, removeCollaborator,
  };
};
